<template>
    <div class="Dashboard">
        <AppNavigation @navigation-toggle="handleNavigationToggle" />
        <Skeleton v-if="!hasFinishedLoading" />

        <div class="Wrap">
            <Infobox
                v-if="!userVerified"
                :hasCloseButton="false"
                title="Email Bestätigen"
                message="Sie haben eine Bestätigungs-E-Mail erhalten. Bitte klicken Sie auf den Link in dieser E-Mail, um Ihre Email-Adresse zu bestätigen."
            >
                <div class="EmailVerifyActions">
                    <Button
                        variant="plain"
                        :isLoading="isLoadingVerifyEmail"
                        @onClick="handleVerifyEmailClick"
                        size="small"
                    >
                        Email Ändern
                    </Button>
                    <Button
                        size="small"
                        :isLoading="isLoadingVerifyEmail"
                        @onClick="handleVerifyEmailClick"
                    >
                        Email Erneut versenden
                    </Button>
                </div>
            </Infobox>
            <router-view
                @onFinishLoading="handleFinishedLoading"
                @onComplete="handleOnboardingComplete"
                key="2"
            />
        </div>
        <Modal
            :show="isModalOpen"
            size="small"
            :title="'Hallo ' + contactPerson"
            :action="{ text: 'Los geht\'s', callback: closeModal }"
            :cancel="{ text: 'Abbrechen', callback: closeModal }"
            @onModalClose="closeModal"
        >
            <p>
                Willkommen in Ihrem neuen Dashboard ✨
                <br />
                <br />
                TaxiFusion hat nun ein neues Aussehen. Doch auch unter der Haube hat sich einiges
                getan! Neben einem schnelleren System haben wir auch neue Funktionen eingeführt. So
                können Sie Falschanmeldungen z.B. nun selbst korrigieren. 📝
                <br /><br />
                Auch eine neue Fahrer-App 📱 ist nun verfügbar! Diese finden Sie
                <a
                    href="https://play.google.com/store/apps/details?id=com.taxifusion.driverApp"
                    target="_blank"
                    rel="noopener noreferrer"
                    style="color: var(--color-blue-light); text-decoration: underline"
                >
                    hier</a
                >
                für Android. <br /><br />
                Ihr TaxiFusion-Team 🙋🏻‍♂️
            </p>
        </Modal>
    </div>
</template>
<script>
import 'remixicon/fonts/remixicon.css';
import { mapActions, mapGetters } from 'vuex';
import 'floating-vue/dist/style.css';
import AppNavigation from '@/components/AppNavigation.vue';
import Skeleton from '@/components/Skeleton.vue';
import Modal from '@/components/widgets/Modal.vue';
import Infobox from '@/components/widgets/Infobox.vue';
import Button from '@/components/widgets/Button.vue';
import axios from 'axios';

export default {
    name: 'dashboard',
    components: {
        AppNavigation,
        Skeleton,
        Modal,
        Infobox,
        Button,
    },
    watch: {
        $route: {
            handler(to, from) {
                if (from && to.path !== from.path) {
                    this.hasFinishedLoading = false;
                }
            },
        },
    },
    data() {
        return {
            profile: {},
            isModalOpen: false,
            hasFinishedLoading: false,
            isLoadingVerifyEmail: false,
        };
    },
    methods: {
        ...mapActions([
            'getProfile',
            'getFullProfile',
            'getEmployees',
            'checkMaintenanceNotification',
        ]),
        handleNavigationToggle(isCollapsed) {
            this.$nextTick(() => {
                this.$forceUpdate();
            });
        },
        async handleVerifyEmailClick() {
            try {
                this.isLoadingVerifyEmail = true;

                await axios.post(
                    `${import.meta.env.VITE_API_BASE_URL}/cpanel/profile/email/request-verify`,
                    null,
                    {
                        withCredentials: true,
                    },
                );
                this.$toasted.success('Email wurden gesendet');
                this.isLoadingVerifyEmail = false;
            } catch (e) {
                this.isLoadingVerifyEmail = false;
            }
        },
        handleFinishedLoading() {
            setTimeout(() => {
                this.hasFinishedLoading = true;
            }, 300);
        },
        closeModal() {
            this.isModalOpen = false;
            localStorage.setItem('isFirstLogin', 'false');
        },
        handleOnboardingComplete() {
            localStorage.setItem('isFirstLogin', 'false');
            this.$router.push('/dashboard');
        },
    },
    computed: {
        ...mapGetters(['contactPerson', 'userVerified', 'user', 'employees']),
    },
    async mounted() {
        const theme = localStorage.getItem('theme') || 'light';
        document.body.classList.add(`theme-${theme}`);

        this.checkMaintenanceNotification();

        try {
            await this.getProfile();
            await this.getFullProfile();
            await this.getEmployees();

            const isFirstLogin = localStorage.getItem('isFirstLogin') === 'true';
            const hasNoEmployees = !this.employees?.some((employee) => !employee?.isSystemDriver);

            if ((isFirstLogin || hasNoEmployees) && this.$route.path !== '/onboarding') {
                this.$router.push('/onboarding');
                return;
            }
            this.isModalOpen = false;
        } catch (error) {
            this.$toasted.show('Ihre Sitzung ist abgelaufen. Bitte melden Sie sich wieder an', {
                type: 'error',
            });
            this.$router.push('/sign-in');
            localStorage.setItem('drivers', JSON.stringify([]));
            localStorage.setItem('cars', JSON.stringify([]));
        }
    },
};
</script>
<style lang="scss">
.Dashboard {
    ::-webkit-scrollbar {
        background-color: var(--color-white);
        width: 10px;
        height: 10px;
    }

    ::-webkit-scrollbar-track {
        background-color: var(--color-white);
    }

    ::-webkit-scrollbar-thumb {
        background-color: var(--color-blue-light);
        border-radius: 16px;
        width: 10px;
        border: 4px solid var(--color-white);
    }

    ::-webkit-scrollbar-button {
        display: none;
    }

    overflow: hidden;
    display: grid;
    grid-template-columns: [sidebar] var(--nav-width, 230px) [main-content] 1fr;
    grid-template-rows: 1fr auto;
    grid-template-areas:
        'sidebar main-content'
        'sidebar sitefooter';
    column-gap: 10px;
    position: relative;
    height: 100vh;

    @media screen and (max-width: $breakpoint-tablet-portrait) {
        display: flex;
        flex-direction: column;
        overflow: visible;
        height: auto;
    }

    h1 {
        line-height: 1.5;
    }

    .Wrap {
        overflow: auto;
        backface-visibility: hidden;
        -webkit-backface-visibility: hidden;

        @media screen and (max-width: $breakpoint-tablet-portrait) {
            overflow: visible;
        }
    }

    .Infobox {
        .EmailVerifyActions {
            display: flex;
            margin-top: 10px;
            gap: 4px;

            @media (max-width: $breakpoint-tablet-landscape) {
                flex-direction: column;
                align-items: flex-start;
            }
        }
    }

    .about {
        @extend %contentWrapper;
        margin-top: 100px;
    }
}
</style>
